/**
 * API client.
 * Toute la communication HTTP avec le backend.
 *
 * - Une seule apiCall<T> gère fetch, timeout, normalisation des erreurs.
 * - Chaque fonction correspond 1:1 à une route backend.
 * - ApiClientError préserve ErrorCode pour le mappage frontend.
 *
 * Injection de credentials IA:
 *   Toutes les routes IA (chat, formula/generate, formula/explain) injectent automatiquement
 *   { provider, model, apiKey } depuis le store de paramètres afin que
 *   les pages n'aient pas besoin de gérer les credentials elles-mêmes.
 *   The provider test route already receives explicit credentials from the
 *   caller (SettingsPage), so it bypasses the auto-injection.
 */

import type {
  ApiResponse,
  ErrorCode,
  ChatRequest,
  ChatResponse,
  FormulaGenerateRequest,
  FormulaGenerateResponse,
  FormulaExplainRequest,
  FormulaExplainResponse,
  DataCleanRequest,
  DataCleanResponse,
  ProviderTestRequest,
  ProviderTestResponse,
  HealthResponse,
  AnalysisRequest,
  AnalysisResponse,
  CustomFunctionRequest,
  CustomFunctionResponse,
  DatasetSchemaDetectRequest,
  DatasetSchemaDetectResponse,
  DatasetStandardizeRequest,
  DatasetStandardizeResponse,
  AutoInsightGenerateRequest,
  AutoInsightGenerateResponse,
  DatasetUnderstandRequest,
  DatasetUnderstandResponse,
  DashboardGeneratePlanRequest,
  DashboardGeneratePlanResponse,
  GenerateReportPlanRequest,
  GenerateReportPlanResponse,
  PlanAgentGoalRequest,
  PlanAgentGoalResponse,
  ExecuteAgentPlanRequest,
  ExecuteAgentPlanResponse,
  RunAgentGoalRequest,
  RunAgentGoalResponse,
} from '@excel-ai-studio/shared';
import { API_ROUTES, LIMITS } from '@excel-ai-studio/shared';
import { getSettings } from '../store/settings.store';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL !== undefined && import.meta.env.VITE_API_BASE_URL !== '')
  ? import.meta.env.VITE_API_BASE_URL
  : '';

/** Erreur levée par le client API. Préserve le ErrorCode du backend. */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly code?: ErrorCode | string,
    public readonly details?: string
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

async function apiCall<T>(path: string, body: unknown): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), LIMITS.REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const json = (await response.json()) as ApiResponse<T>;

    if (!json.success) {
      throw new ApiClientError(json.error.message, json.error.code, json.error.details);
    }

    return json.data;
  } catch (err) {
    if (err instanceof ApiClientError) throw err;
    if ((err as Error).name === 'AbortError') {
      throw new ApiClientError('La requete a expire. Veuillez reessayer.', 'REQUEST_TIMEOUT');
    }
    throw new ApiClientError((err as Error).message, 'UNKNOWN_ERROR');
  } finally {
    clearTimeout(timeout);
  }
}

// ─── AI credentials injection ────────────────────────────

/**
 * Read current settings and return the fields needed by every AI request.
 * Called lazily at request time so changes are picked up immediately.
 */
function getAICredentials(): { provider: string; model: string; apiKey: string } {
  const s = getSettings();
  return { provider: s.provider, model: s.model, apiKey: s.apiKey };
}

// ─── Public API ──────────────────────────────────────────

export function sendChat(request: ChatRequest): Promise<ChatResponse> {
  return apiCall<ChatResponse>(API_ROUTES.CHAT, { ...getAICredentials(), ...request });
}

export function generateFormula(request: FormulaGenerateRequest): Promise<FormulaGenerateResponse> {
  return apiCall<FormulaGenerateResponse>(API_ROUTES.FORMULA_GENERATE, { ...getAICredentials(), ...request });
}

export function explainFormula(request: FormulaExplainRequest): Promise<FormulaExplainResponse> {
  return apiCall<FormulaExplainResponse>(API_ROUTES.FORMULA_EXPLAIN, { ...getAICredentials(), ...request });
}

export function cleanData(request: DataCleanRequest): Promise<DataCleanResponse> {
  return apiCall<DataCleanResponse>(API_ROUTES.DATA_CLEAN, request);
}

export function testProvider(request: ProviderTestRequest): Promise<ProviderTestResponse> {
  return apiCall<ProviderTestResponse>(API_ROUTES.PROVIDER_TEST, request);
}

export async function checkHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}${API_ROUTES.HEALTH}`);
    const json = (await res.json()) as ApiResponse<HealthResponse>;
    return json.success;
  } catch {
    return false;
  }
}

export function analyzeDataset(request: AnalysisRequest): Promise<AnalysisResponse> {
  return apiCall<AnalysisResponse>(API_ROUTES.ANALYSIS, { ...getAICredentials(), ...request });
}

export function executeCustomFunction(request: CustomFunctionRequest): Promise<CustomFunctionResponse> {
  return apiCall<CustomFunctionResponse>(API_ROUTES.CUSTOM_FUNCTION, { ...getAICredentials(), ...request });
}

export function detectDatasetSchema(request: DatasetSchemaDetectRequest): Promise<DatasetSchemaDetectResponse> {
  return apiCall<DatasetSchemaDetectResponse>(API_ROUTES.DATASET_SCHEMA_DETECT, request);
}

export function standardizeDataset(request: DatasetStandardizeRequest): Promise<DatasetStandardizeResponse> {
  return apiCall<DatasetStandardizeResponse>(API_ROUTES.DATASET_STANDARDIZE, request);
}

export function generateAutoInsights(request: AutoInsightGenerateRequest): Promise<AutoInsightGenerateResponse> {
  return apiCall<AutoInsightGenerateResponse>(API_ROUTES.AUTO_INSIGHTS_GENERATE, { ...getAICredentials(), ...request });
}

export function understandDataset(request: DatasetUnderstandRequest): Promise<DatasetUnderstandResponse> {
  return apiCall<DatasetUnderstandResponse>(API_ROUTES.DATASET_UNDERSTAND, request);
}

export function generateDashboardPlan(request: DashboardGeneratePlanRequest): Promise<DashboardGeneratePlanResponse> {
  return apiCall<DashboardGeneratePlanResponse>(API_ROUTES.DASHBOARD_GENERATE_PLAN, request);
}

export function generateReportPlan(request: GenerateReportPlanRequest): Promise<GenerateReportPlanResponse> {
  return apiCall<GenerateReportPlanResponse>(API_ROUTES.REPORT_GENERATE_PLAN, request);
}

export function generateSpreadsheetPlan(request: { prompt: string; sheetName?: string }): Promise<{ plan: any }> {
  return apiCall<{ plan: any }>(API_ROUTES.SPREADSHEET_GENERATE_PLAN, request);
}

export function planAgentGoal(request: PlanAgentGoalRequest): Promise<PlanAgentGoalResponse> {
  return apiCall<PlanAgentGoalResponse>(API_ROUTES.AGENT_PLAN, { ...getAICredentials(), ...request });
}

export function executeAgentPlan(request: ExecuteAgentPlanRequest): Promise<ExecuteAgentPlanResponse> {
  return apiCall<ExecuteAgentPlanResponse>(API_ROUTES.AGENT_EXECUTE, request);
}

export function runAgentGoal(request: RunAgentGoalRequest): Promise<RunAgentGoalResponse> {
  return apiCall<RunAgentGoalResponse>(API_ROUTES.AGENT_RUN, { ...getAICredentials(), ...request });
}
